import { useCADPageStore, WAND } from '@/state'
import { Camera, Canvas } from '@react-three/fiber'
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react'
import { COLOR_OPTIONS } from '@/constants'

import { Scene } from '@/pages/CADPage/components/CADScene/CADScene'
import { eventManagerFactory } from '@/pages/CADPage/components/CADModel/hooks/useCanvasEventManager'
import { useCADQuery } from '@/services/queries/cads'
import { GLTFObject } from './GLTFObject'

/**
 * We preflight WebGL support before mounting react-three-fiber's <Canvas>.
 * If the browser cannot create a WebGL context, mounting <Canvas> throws and
 * trips the app-level Sentry ErrorBoundary. This check prevents that throw.
 */
const canInitializeWebGL = () => {
  if (typeof document === 'undefined') return false

  try {
    const testCanvas = document.createElement('canvas')
    const attrs: WebGLContextAttributes = {
      antialias: true,
      preserveDrawingBuffer: true,
      stencil: true,
    }
    const context =
      testCanvas.getContext('webgl2', attrs) ||
      testCanvas.getContext('webgl', attrs)

    // Release the test context immediately when supported.
    context?.getExtension?.('WEBGL_lose_context')?.loseContext?.()

    return Boolean(context)
  } catch {
    return false
  }
}

type CanvasContextType = {
  camera: Camera | null
  gltf: GLTFObject | undefined
  canvasID: string | null
  setCanvasID: (id: string | null) => void
  setCamera: (camera: Camera | null) => void
  setGLTF: (gltf: GLTFObject | undefined) => void
  setCanvasBounds: (
    bounds: { x: number; y: number; width: number; height: number } | null,
  ) => void
  setCanvasVisible: (visible: boolean) => void
}

export const CanvasContext = createContext<CanvasContextType>({
  camera: null,
  gltf: undefined,
  canvasID: null,
  setCanvasID: () => {},
  setCamera: () => {},
  setGLTF: () => {},
  setCanvasBounds: () => {},
  setCanvasVisible: () => {},
})

export const useCanvasContext = () => useContext(CanvasContext)

export const CanvasProvider = ({ children }: { children: ReactNode }) => {
  const { data } = useCADQuery()
  const cadVersionId = data?.version?.id
  const isWebGLSupported = useMemo<boolean>(() => canInitializeWebGL(), [])

  const [canvasBounds, setCanvasBounds] = useState<{
    x: number
    y: number
    width: number
    height: number
  } | null>(null)
  const [canvasVisible, setCanvasVisible] = useState(false)
  const [camera, setCamera] = useState<Camera | null>(null)
  const [canvasID, setCanvasID] = useState<string | null>(null)
  const wandSelected = useCADPageStore((state) => state.wandSelected)

  const gltfRef = useRef<GLTFObject | undefined>(undefined)

  const setGLTF = useCallback((gltf: GLTFObject | undefined) => {
    if (gltfRef.current && gltfRef.current !== gltf) {
      gltfRef.current.dispose()
    }
    gltfRef.current = gltf
  }, [])

  // Invalidate cache when cadVersionId changes
  useEffect(() => {
    if (cadVersionId && canvasID && cadVersionId !== canvasID) {
      if (gltfRef.current) {
        gltfRef.current.dispose()
        gltfRef.current = undefined
      }
      setCanvasID(cadVersionId)
    } else if (cadVersionId && !canvasID) {
      setCanvasID(cadVersionId)
    }
  }, [cadVersionId, canvasID])

  useEffect(() => {
    return () => {
      if (gltfRef.current) {
        gltfRef.current.dispose()
        gltfRef.current = undefined
      }
    }
  }, [])

  const contextValue = useMemo(
    () => ({
      get gltf() {
        return gltfRef.current
      },
      camera,
      setCamera,
      setCanvasBounds,
      setCanvasVisible,
      setGLTF,
      canvasID,
      setCanvasID,
    }),
    [setGLTF, camera, canvasID],
  )

  const currentColorName = useMemo(() => {
    if (wandSelected === WAND.TRANSPARENCY) {
      return WAND.TRANSPARENCY
    }

    if (wandSelected) {
      const selectedColor = COLOR_OPTIONS.find(
        (color) => color.value === parseInt(wandSelected.slice(1), 16),
      )
      return selectedColor?.name
    }
  }, [wandSelected])

  return (
    <CanvasContext.Provider value={contextValue}>
      <div
        style={{
          position: 'fixed',
          left: canvasBounds?.x ?? 0,
          top: canvasBounds?.y ?? 0,
          width: canvasBounds?.width ?? 0,
          height: canvasBounds?.height ?? 0,
          visibility: canvasVisible && canvasBounds ? 'visible' : 'hidden',
          pointerEvents: canvasVisible && canvasBounds ? 'auto' : 'none',
          zIndex: 10,
        }}
      >
        {isWebGLSupported ? (
          <Canvas
            id="cad-canvas"
            data-testid="cad-page-canvas"
            events={eventManagerFactory}
            className={
              currentColorName ? `cursor-wand__${currentColorName}` : ''
            }
            fallback={<div>Loading...</div>}
            style={{
              width: '100%',
              height: '100%',
            }}
            gl={{
              toneMappingExposure: 0.8,
              preserveDrawingBuffer: true,
              localClippingEnabled: true,
              antialias: true,
              stencil: true,
            }}
            frameloop={canvasVisible ? 'demand' : 'never'}
            performance={{ min: 0.5 }}
            dpr={[1, 2]}
            onPointerDown={() => useCADPageStore.setState({ isRotating: true })}
            onPointerUp={() => useCADPageStore.setState({ isRotating: false })}
          >
            <Scene onCameraRef={setCamera} />
          </Canvas>
        ) : (
          canvasVisible && (
            <div className="w-full h-full flex items-center justify-center rounded-md border bg-gray-50 p-4 text-center text-sm text-gray-700">
              Your browser does not have WebGL enabled. Please try a different
              browser, update your current browser, or enable WebGL in browser
              settings.
            </div>
          )
        )}
      </div>
      {children}
    </CanvasContext.Provider>
  )
}

export default CanvasProvider
